import { useCallback, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext, UserContextType } from "../../Context/UserContext";
import {
  clearAuthSession,
  getStoredAccessToken,
  isAccessTokenExpired,
  isAuthEnabled,
} from "../../Utils/authSession";

const SESSION_CHECK_INTERVAL_MS = 60_000;

/**
 * Keeps React auth state in sync when the JWT expires client-side (no API round-trip).
 */
export const AuthSessionMonitor = () => {
  const { user, setUser } = useContext<UserContextType>(UserContext);
  const navigate = useNavigate();

  const enforceValidSession = useCallback(() => {
    if (!isAuthEnabled() || !user) {
      return;
    }
    const token = getStoredAccessToken();
    if (!token || isAccessTokenExpired(token)) {
      clearAuthSession();
      setUser(false);
      navigate("/login", { replace: true });
    }
  }, [user, setUser, navigate]);

  useEffect(() => {
    enforceValidSession();
  }, [enforceValidSession]);

  useEffect(() => {
    if (!user) {
      return undefined;
    }

    const intervalId = window.setInterval(
      enforceValidSession,
      SESSION_CHECK_INTERVAL_MS
    );

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        enforceValidSession();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [user, enforceValidSession]);

  return null;
};
